import { fabric } from 'fabric';
import { parseGIF, decompressFrame } from 'gifuct-js';
import {
    startRenderLoop,
    stopRenderLoop,
    incrementActive,
    decrementActive,
    getActiveCount,
    registerTicker,
    unregisterTicker,
} from '~/scripts/utils/canvas-render-loop';

// Hybrid GIF widget for canvas playback.
//
// Decode strategy is chosen per-instance via feature detection:
//   - ImageDecoder API (WebCodecs)  → modern Chromium ≥94, Firefox ≥133
//   - gifuct-js fallback            → legacy Chromium (Samsung SSSP / Tizen)
//
// Both paths render decoded frames onto a private offscreen canvas, which is
// what fabric.Image uses as its source element. A shared RAF tick advances
// the current frame index based on per-frame delay; fabric is asked to
// redraw only when a frame actually advanced.

let _imageDecoderSupportPromise = null;

function canUseImageDecoder(forceFallback) {
    if (forceFallback) return Promise.resolve(false);
    if (_imageDecoderSupportPromise) return _imageDecoderSupportPromise;
    if (typeof ImageDecoder === 'undefined') {
        _imageDecoderSupportPromise = Promise.resolve(false);
        return _imageDecoderSupportPromise;
    }
    _imageDecoderSupportPromise = ImageDecoder.isTypeSupported('image/gif')
        .then((ok) => !!ok)
        .catch(() => false);
    return _imageDecoderSupportPromise;
}

const GifElement = fabric.util.createClass(fabric.Image, {
    type: 'gifElement',
    superType: 'widget',
    objectCaching: false,
    lockScalingFlip: true,

    backend: null, // 'image-decoder' | 'gifuct'
    customData: null,

    initialize: function (element, options = {}) {
        const customData = options.customData || {};

        const w =
            parseInt(options.width, 10) ||
            (element && element.width) ||
            480;
        const h =
            parseInt(options.height, 10) ||
            (element && element.height) ||
            270;

        const offscreen = document.createElement('canvas');
        offscreen.width = w;
        offscreen.height = h;
        this._offscreen = offscreen;
        this._offscreenCtx = offscreen.getContext('2d');
        if (this._offscreenCtx) {
            this._offscreenCtx.fillStyle = 'rgba(200, 200, 200, 0.3)';
            this._offscreenCtx.fillRect(0, 0, w, h);
        }

        this.callSuper('initialize', offscreen, {
            ...options,
            width: w,
            height: h,
            objectCaching: false,
        });

        this.customData = customData;

        // ImageDecoder backend state
        this._decoder = null;
        this._videoFrames = null;

        // gifuct-js backend state
        this._frames = null;
        this._patchCanvas = null;
        this._patchCtx = null;
        this._restoreSnapshot = null;
        this._pendingDisposal = 0;
        this._pendingDisposalRect = null;

        // Shared playback state
        this._currentFrameIndex = 0;
        this._frameStartTime = 0;
        this._isActive = false;
        this._pendingRenderLoop = false;
        this._ticker = null;

        // fabric fires 'removed' on the object when canvas.remove() is called.
        // There is no method-name auto-hook (no onRemoved() callback), so we
        // must subscribe explicitly or cleanup never runs and VideoFrames /
        // tickers leak.
        this.on('removed', () => this._cleanup());

        if (customData.posterSrc) {
            this._loadPoster(customData.posterSrc);
        }
        if (customData.gifSrc) {
            this._loadGif(customData.gifSrc, !!customData.forceFallback);
        }
    },

    _loadPoster(url) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            // Bail if real frames already loaded.
            if (this._videoFrames || this._frames) return;
            const ctx = this._offscreenCtx;
            const offscreen = this._offscreen;
            if (!ctx || !offscreen) return;
            ctx.clearRect(0, 0, offscreen.width, offscreen.height);
            ctx.drawImage(img, 0, 0, offscreen.width, offscreen.height);
            this.dirty = true;
            if (this.canvas) this.canvas.requestRenderAll();
        };
        img.src = url;
    },

    async _loadGif(url, forceFallback) {
        try {
            const useImageDecoder = await canUseImageDecoder(forceFallback);
            if (useImageDecoder) {
                this.backend = 'image-decoder';
                await this._loadGifWithImageDecoder(url);
            } else {
                this.backend = 'gifuct';
                await this._loadGifWithGifuct(url);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[GifElement] decode failed', e);
        }
    },

    async _loadGifWithImageDecoder(url) {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok || !response.body) {
            throw new Error(`fetch failed: ${response.status}`);
        }
        const decoder = new ImageDecoder({
            data: response.body,
            type: 'image/gif',
        });
        await decoder.completed;
        this._decoder = decoder;

        const track = decoder.tracks.selectedTrack;
        if (!track) throw new Error('no selected track');

        const frameCount = track.frameCount;
        const videoFrames = [];
        for (let i = 0; i < frameCount; i++) {
            const result = await decoder.decode({ frameIndex: i });
            const image = result.image;
            const durationUs = image.duration;
            videoFrames.push({
                image,
                duration:
                    typeof durationUs === 'number' && durationUs > 0
                        ? durationUs / 1000
                        : 100,
            });
        }
        this._videoFrames = videoFrames;

        const first = videoFrames[0].image;
        const gifW = first.displayWidth;
        const gifH = first.displayHeight;
        this._resizeOffscreen(gifW, gifH);

        this._currentFrameIndex = 0;
        this._frameStartTime = performance.now();
        this._renderFrame(0);
        this._adjustDisplaySize(gifW, gifH);
        this.dirty = true;
        this._startActive();
        if (this.canvas) this.canvas.requestRenderAll();
    },

    async _loadGifWithGifuct(url) {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) {
            throw new Error(`fetch failed: ${response.status}`);
        }
        const buffer = await response.arrayBuffer();
        const decoded = decodeGifSync(buffer);
        this._frames = decoded.frames;

        this._patchCanvas = document.createElement('canvas');
        this._patchCtx = this._patchCanvas.getContext('2d');

        this._resizeOffscreen(decoded.width, decoded.height);

        this._currentFrameIndex = 0;
        this._frameStartTime = performance.now();
        this._pendingDisposal = 0;
        this._pendingDisposalRect = null;
        this._restoreSnapshot = null;
        this._renderFrame(0);

        this._adjustDisplaySize(decoded.width, decoded.height);
        this.dirty = true;
        this._startActive();
        if (this.canvas) this.canvas.requestRenderAll();
    },

    _resizeOffscreen(w, h) {
        const offscreen = this._offscreen;
        if (!offscreen) return;
        offscreen.width = w;
        offscreen.height = h;
        this._offscreenCtx = offscreen.getContext('2d');
    },

    _adjustDisplaySize(gifW, gifH) {
        // Preserve the originally-requested display size by adjusting scale,
        // since fabric.Image's source dimensions just changed to native GIF size.
        const displayW = this.width * this.scaleX;
        const displayH = this.height * this.scaleY;
        this.width = gifW;
        this.height = gifH;
        this.scaleX = displayW / gifW;
        this.scaleY = displayH / gifH;
        this.setCoords();
    },

    _renderFrame(index) {
        if (this.backend === 'image-decoder') {
            this._renderFrameImageDecoder(index);
        } else if (this.backend === 'gifuct') {
            this._renderFrameGifuct(index);
        }
    },

    _renderFrameImageDecoder(index) {
        const ctx = this._offscreenCtx;
        const offscreen = this._offscreen;
        const frames = this._videoFrames;
        if (!ctx || !offscreen || !frames) return;
        ctx.clearRect(0, 0, offscreen.width, offscreen.height);
        ctx.drawImage(frames[index].image, 0, 0);
    },

    _renderFrameGifuct(index) {
        const ctx = this._offscreenCtx;
        const offscreen = this._offscreen;
        const frames = this._frames;
        const patchCanvas = this._patchCanvas;
        const patchCtx = this._patchCtx;
        if (!ctx || !offscreen || !frames || !patchCanvas || !patchCtx) return;

        const frame = frames[index];

        if (this._pendingDisposal === 2 && this._pendingDisposalRect) {
            const r = this._pendingDisposalRect;
            ctx.clearRect(r.x, r.y, r.w, r.h);
        } else if (this._pendingDisposal === 3 && this._restoreSnapshot) {
            ctx.putImageData(this._restoreSnapshot, 0, 0);
            this._restoreSnapshot = null;
        }

        if (frame.disposalType === 3) {
            this._restoreSnapshot = ctx.getImageData(
                0,
                0,
                offscreen.width,
                offscreen.height
            );
        }

        patchCanvas.width = frame.dims.width;
        patchCanvas.height = frame.dims.height;
        const patchData = patchCtx.createImageData(
            frame.dims.width,
            frame.dims.height
        );
        patchData.data.set(frame.patch);
        patchCtx.putImageData(patchData, 0, 0);
        ctx.drawImage(patchCanvas, frame.dims.left, frame.dims.top);

        this._pendingDisposal = frame.disposalType;
        this._pendingDisposalRect =
            frame.disposalType === 2
                ? {
                      x: frame.dims.left,
                      y: frame.dims.top,
                      w: frame.dims.width,
                      h: frame.dims.height,
                  }
                : null;
    },

    _frameDuration(index) {
        if (this.backend === 'image-decoder') {
            return this._videoFrames ? this._videoFrames[index].duration : 0;
        }
        if (this.backend === 'gifuct') {
            return this._frames ? this._frames[index].delay : 0;
        }
        return 0;
    },

    _frameCount() {
        if (this.backend === 'image-decoder') {
            return this._videoFrames ? this._videoFrames.length : 0;
        }
        if (this.backend === 'gifuct') {
            return this._frames ? this._frames.length : 0;
        }
        return 0;
    },

    // Returns true if a frame advanced — RAF loop redraws only when true.
    _tickFrame() {
        const total = this._frameCount();
        if (total <= 1) return false;
        const now = performance.now();
        const currentDuration = this._frameDuration(this._currentFrameIndex);
        if (now - this._frameStartTime < currentDuration) return false;
        this._currentFrameIndex = (this._currentFrameIndex + 1) % total;
        this._frameStartTime = now;
        this._renderFrame(this._currentFrameIndex);
        this.dirty = true;
        return true;
    },

    _startActive() {
        if (this._isActive) return;
        this._isActive = true;
        if (!this._ticker) this._ticker = () => this._tickFrame();
        registerTicker(this._ticker);
        incrementActive();
        if (getActiveCount() === 1) {
            if (this.canvas) {
                startRenderLoop(this.canvas);
            } else {
                this._pendingRenderLoop = true;
            }
        }
    },

    _stopActive() {
        if (!this._isActive) return;
        this._isActive = false;
        if (this._ticker) unregisterTicker(this._ticker);
        decrementActive();
        if (getActiveCount() === 0) {
            stopRenderLoop();
        }
    },

    _render(ctx) {
        if (this._pendingRenderLoop && this.canvas) {
            this._pendingRenderLoop = false;
            startRenderLoop(this.canvas);
        }
        this.callSuper('_render', ctx);
    },

    _cleanup() {
        this._stopActive();
        if (this._videoFrames) {
            for (const f of this._videoFrames) {
                if (f.image && typeof f.image.close === 'function') {
                    f.image.close();
                }
            }
            this._videoFrames = null;
        }
        if (this._decoder && typeof this._decoder.close === 'function') {
            this._decoder.close();
        }
        this._decoder = null;
        this._frames = null;
        this._patchCanvas = null;
        this._patchCtx = null;
        this._restoreSnapshot = null;
        this._pendingDisposalRect = null;
        this._offscreen = null;
        this._offscreenCtx = null;
        this._ticker = null;
    },

    toObject(extra) {
        return Object.assign(this.callSuper('toObject', extra), {
            customData: this.customData,
            superType: this.superType,
        });
    },
});

GifElement.fromObject = function (object, callback) {
    callback(new GifElement(null, { ...object }));
};
GifElement.async = true;

// Synchronous gifuct-js decode. The player POC isn't dealing with massive
// GIFs in the same render frame, so a yield-aware loop isn't required here.
// Add yield-every-N-frames if a real device hits long-task warnings.
function decodeGifSync(buffer) {
    const gif = parseGIF(buffer);
    const frames = [];
    for (const rawFrame of gif.frames) {
        if (!('image' in rawFrame)) continue;
        const decoded = decompressFrame(rawFrame, gif.gct, true);
        frames.push({
            dims: decoded.dims,
            patch: decoded.patch,
            delay: decoded.delay > 0 ? decoded.delay : 100,
            disposalType: decoded.disposalType,
        });
    }
    return {
        width: gif.lsd.width,
        height: gif.lsd.height,
        frames,
    };
}

export default GifElement;
